import { useEffect, useMemo, useState } from 'react';
import AggregrationDropZone from '../DropZones/AggregrationDropZone';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import {
  Box,
  Divider,
  FormControl,
  ListItem,
  MenuItem,
  Select,
  Typography,
  Tooltip,
} from '@mui/material';
import FilterOverride from './FilterOverride/FilterOverride';
import DimensionOverride from './DimensionOverride';
import { fontSize, palette } from '../../..';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  addSource,
  deleteField,
  setAggregationValue,
} from '../../../redux/Calculations/CalculationsActions';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { aggregations } from '../constants';
import { isFlowUsed } from '../utils';
import { checkIfMissingColumn } from '../../CommonFunctions/CommonFunctions';
import { useEnsureNodeVisible } from '../../../hooks/useEnsureNodeVisible';
interface ISummarize {
  propKey: string;
  isFlowUsedInOtherFlowsAsSource: boolean;
}

const shouldShowAdvancedOption = (dimension: any, filter: any) => {
  if (dimension.type === 'ignoreAll' || filter.type === 'ignoreAll') return true;

  if (dimension.type.endsWith('Some') && dimension?.dimensions.length > 0) {
    return true;
  }

  if (filter.type.endsWith('Some')) {
    if (
      filter?.filters.length > 0 ||
      filter?.filterGroups?.length > 0 ||
      filter?.filterPanel?.filters?.length > 0
    )
      return true;
  }

  return false;
};

function Summarize({ propKey, isFlowUsedInOtherFlowsAsSource }: ISummarize) {
  const dispatch = useDispatch();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { calculationInfo, activeFlow, flowNames, selectedDs, tablesForSelectedDataSets } =
    useSelector((state: RootState) => {
      const currCalc = state.calculations.properties[propKey]?.currentCalculationSession;
      return {
        calculationInfo: currCalc?.calculationInfo || null,
        activeFlow: currCalc?.activeFlow || null,
        flowNames: currCalc?.flowDisplayNames || {},
        selectedDs: state.chartProperties.properties[propKey]?.selectedDs,
        tablesForSelectedDataSets: state.tabTileProps.tablesForSelectedDataSets,
      };
    });
  const [isShowWarning, setIsShowWarning] = useState(false);
  const ensureNodeVisible = useEnsureNodeVisible();
  const currentFlow = activeFlow ? calculationInfo?.flows?.[activeFlow] : undefined;

  const step0 = currentFlow?.[0];
  const overrideInfo = step0?.flowOverride;
  const source = step0?.source ?? [];

  const disableUpdate = useMemo(() => {
    if (!calculationInfo || !activeFlow) return true;
    return isFlowUsed(
      Object.keys(calculationInfo.flows),
      calculationInfo.flows,
      activeFlow,
      calculationInfo,
    );
  }, [calculationInfo, activeFlow]);




  useEffect(() => {
    if (!showAdvancedOptions || !activeFlow) return;
    ensureNodeVisible(activeFlow);
  }, [showAdvancedOptions]);



  const getInfo = (id: string, type: string) => {
    if (!id || !type || !calculationInfo) return null;
    if (type === 'field')
      return {
        name:
          calculationInfo.fields[id]?.displayName || calculationInfo.fields[id]?.fieldName || id,
        dataType: calculationInfo.fields[id]?.dataType || 'unknown',
      };
    else if (type === 'flow')
      return {
        name: flowNames[id] ?? id,
        dataType: calculationInfo.flows[id]?.[0]?.resultantDatatype || 'unknown',
      };
    return null;
  };
  const disabledAggregation = (mathOperation?: boolean) => {
    if (!currentFlow) return false;

    const info = getInfo(currentFlow[0].source[0], currentFlow[0].sourceType[0]);
    if (!info) return true;
    return mathOperation && !['integer', 'decimal'].includes(info.dataType);
  };

  const handleAggregationChange = (event: any) => {
    if (disableUpdate || !activeFlow) return;
    const value = event.target.value as string;
    if (!value) return;
    dispatch(setAggregationValue(propKey, activeFlow, 0, value));
  };

  const handleAdvancedOptionClick = () => setShowAdvancedOptions(true);

  useEffect(() => {
    if (!overrideInfo) return;

    const advancedOption = shouldShowAdvancedOption(overrideInfo?.dimension, overrideInfo?.filter);
    if (advancedOption) {
      setShowAdvancedOptions(advancedOption);
    }
  }, []);

  useEffect(() => {
    if (!step0 || !currentFlow||!calculationInfo) return;
    if (currentFlow[0].sourceType[0] !== 'field') return;

    (async () => {
      const res = await checkIfMissingColumn(
        calculationInfo.fields[currentFlow[0].source[0]],
        selectedDs,
        tablesForSelectedDataSets[selectedDs.id],
      );
      setIsShowWarning(res.isMissing);
    })();
  }, []);

  if (!step0) return null;

  return (
    <Box
      id="summarize"
      sx={{
        paddingLeft: '0.5rem',
        paddingRight: '0.35rem',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AggregrationDropZone propKey={propKey}>
        <Box sx={{ display: "flex", alignItems: !source[0] ? 'center' : "baseline", justifyContent: "center" }}>
          {!source[0] ? (
            <Typography
              sx={{
                color: '#484848',
                fontSize: fontSize.extraSmall,
                textAlign: 'center',
                py: 1.5
              }}
            >
              Drag a field or flow here
            </Typography>
          ) : (
            <ListItem
              sx={{
                border: isShowWarning ? '1px solid #850000ff' : '1px solid grey',
                borderRadius: '5px',
                paddingInline: '0.3rem',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '1.5rem',
                width: '100%',
                gap: '0.2rem',
                boxSizing: 'border-box',
                marginBottom: '1rem',
              }}
            >
              {isFlowUsedInOtherFlowsAsSource ? (
                <Tooltip
                  title="This calculation is being used in another flow. Please remove the dependency before deleting."
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <LockOutlinedIcon
                    style={{
                      color: '#2BB9BB',
                    }}
                  />
                </Tooltip>
              ) : (
                <CloseRoundedIcon
                  onClick={() => {
                    if (disableUpdate || !activeFlow) return;
                    let fieldId = null;
                    if (currentFlow[0].sourceType[0] === 'field')
                      fieldId = currentFlow[0].source[0];
                    dispatch(addSource(propKey, activeFlow, 0, undefined, 'undefined', 0));
                    if (fieldId) dispatch(deleteField(propKey, fieldId));
                  }}
                  sx={{
                    fontSize: fontSize.medium,
                    cursor: 'pointer',
                    color: palette.primary.contrastText,
                    '&:hover': {
                      color: disableUpdate ? 'inherit' : 'red',
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              )}
              <span
                title={getInfo(currentFlow[0].source[0], currentFlow[0].sourceType[0])?.name}
                style={{
                  width: '100px',
                  flexGrow: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: palette.primary.contrastText,
                }}
              >
                {getInfo(currentFlow[0].source[0], currentFlow[0].sourceType[0])?.name}
              </span>
              <FormControl
                sx={{
                  maxWidth: 120,
                  flexShrink: 0,
                  marginLeft: 'auto',
                }}
              >
                <Select
                  title={currentFlow[0].aggregation[0] as string | undefined}
                  value={currentFlow[0].aggregation[0]}
                  disabled={disableUpdate}
                  sx={{
                    marginLeft: 'auto',
                    height: '1.5rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    textAlign: 'left',
                    '.MuiSelect-icon': {
                      fontSize: '19px',
                      marginLeft: '20px',
                      color: palette.primary.contrastText,
                      right: '1px',
                    },
                    fontSize: fontSize.medium,
                  }}
                  onChange={handleAggregationChange}
                  IconComponent={KeyboardArrowDownIcon}
                >
                  {aggregations.map((aggregation, _index: number) => {
                    return (
                      <MenuItem
                        disabled={disabledAggregation(aggregation.mathOperation)}
                        sx={{
                          fontSize: fontSize.small,
                          color: 'primary.contrastText',
                          height: '1.5rem',
                        }}
                        key={_index}
                        value={aggregation.value}
                      >
                        {aggregation.displayName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </ListItem>
          )}
        </Box>
      </AggregrationDropZone>
      <Box
        id="summarize-override-wrapper"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <Box sx={{ display: showAdvancedOptions ? 'none' : 'block' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={handleAdvancedOptionClick}
          >
            <Tooltip
              title={
                <span style={{ fontSize: fontSize.medium }}>
                  Show filter override and dimension override.
                </span>
              }
            >
              <Typography
                sx={{
                  color: '#484848',
                  fontSize: fontSize.small,
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'underline',
                  my: "0.5rem",
                  gap: '0.25rem',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                Override options
              </Typography>
            </Tooltip>
          </Box>
        </Box>
        {showAdvancedOptions && overrideInfo && (
          <>
            <FilterOverride
              filterOverrideInfo={overrideInfo.filter}
              propKey={propKey}
              sx={{ marginBottom: '0.5rem' }}
              ensureNodeVisible={() => activeFlow && ensureNodeVisible(activeFlow)}
            />
            <Divider />
            <DimensionOverride
              propKey={propKey}
              sx={{ flexGrow: 1, marginTop: '0.5rem' }}
              dimensionOverrideInfo={overrideInfo.dimension}
              ensureNodeVisible={() => activeFlow && ensureNodeVisible(activeFlow)}
            />
          </>
        )}
      </Box>
    </Box>
  );
}

export default Summarize;
